// collision.js — géométrie de collision de l'auto-tamponneuse (Car.dc.html)
// Repère local : viewBox 144×204, centre (72,102), nez vers -y. angle en radians, sens horaire (comme CSS rotate).
// Contour exact (trait de 5 inclus) : rectangle arrondi x∈[1.5,142.5], y∈[1.5,202.5], rayon 64.5.
// Capsule quasi exacte (écart max ≈ 0.2 unité) : segment vertical ±31.5 autour du centre, rayon 70.5.
// Échelle jeu : scale = longueurAffichéeDeLaVoiture / 204.
// Une voiture = { x, y, angle, scale }

export const CAR = { W: 141, H: 201, HALF_LEN: 31.5, RADIUS: 70.5 };

// Path SVG du contour exact (repère 144×204) — pour le debug visuel
export const HULL_PATH = 'M66 1.5 H78 A64.5 64.5 0 0 1 142.5 66 V138 A64.5 64.5 0 0 1 78 202.5 H66 A64.5 64.5 0 0 1 1.5 138 V66 A64.5 64.5 0 0 1 66 1.5 Z';

// Polygone du contour exact, centré (0,0) — pour un moteur physique (SAT, Box2D…)
export function hullPolygon(segsPerCorner = 4, scale = 1) {
  const r = 64.5, corners = [[6, -36], [6, 36], [-6, 36], [-6, -36]], start = [-Math.PI / 2, 0, Math.PI / 2, Math.PI];
  const pts = [];
  corners.forEach(([cx, cy], i) => {
    for (let k = 0; k <= segsPerCorner; k++) {
      const a = start[i] + (Math.PI / 2) * k / segsPerCorner;
      pts.push([(cx + r * Math.cos(a)) * scale, (cy + r * Math.sin(a)) * scale]);
    }
  });
  return pts;
}

// Extrémités du segment de la capsule, en coordonnées monde
export function capsuleEnds(car) {
  const d = CAR.HALF_LEN * (car.scale ?? 1), sin = Math.sin(car.angle || 0), cos = Math.cos(car.angle || 0);
  return [car.x + d * sin, car.y - d * cos, car.x - d * sin, car.y + d * cos];
}

export function carRadius(car) { return CAR.RADIUS * (car.scale ?? 1); }

function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

function closestOnSeg(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1, L = dx * dx + dy * dy;
  const t = L ? clamp01(((px - x1) * dx + (py - y1) * dy) / L) : 0;
  return [x1 + t * dx, y1 + t * dy];
}

// Points les plus proches entre deux segments [p sur A, q sur B]
function segSegClosest(ax1, ay1, ax2, ay2, bx1, by1, bx2, by2) {
  const d1x = ax2 - ax1, d1y = ay2 - ay1, d2x = bx2 - bx1, d2y = by2 - by1;
  const rx = ax1 - bx1, ry = ay1 - by1;
  const a = d1x * d1x + d1y * d1y, e = d2x * d2x + d2y * d2y, f = d2x * rx + d2y * ry;
  let s, t;
  if (a <= 1e-9 && e <= 1e-9) { s = 0; t = 0; }
  else if (a <= 1e-9) { s = 0; t = clamp01(f / e); }
  else {
    const c = d1x * rx + d1y * ry;
    if (e <= 1e-9) { t = 0; s = clamp01(-c / a); }
    else {
      const b = d1x * d2x + d1y * d2y, den = a * e - b * b;
      s = den ? clamp01((b * f - c * e) / den) : 0;
      t = (b * s + f) / e;
      if (t < 0) { t = 0; s = clamp01(-c / a); }
      else if (t > 1) { t = 1; s = clamp01((b - c) / a); }
    }
  }
  return [ax1 + s * d1x, ay1 + s * d1y, bx1 + t * d2x, by1 + t * d2y];
}

// Voiture vs voiture → {hit, dist, depth, nx, ny} — normale de a vers b, depth = enfoncement
export function carVsCar(a, b) {
  const [p, q] = [capsuleEnds(a), capsuleEnds(b)];
  const [px, py, qx, qy] = segSegClosest(p[0], p[1], p[2], p[3], q[0], q[1], q[2], q[3]);
  const dx = qx - px, dy = qy - py, dist = Math.hypot(dx, dy), sum = carRadius(a) + carRadius(b);
  let nx, ny;
  if (dist > 1e-6) { nx = dx / dist; ny = dy / dist; }
  else { const cd = Math.hypot(b.x - a.x, b.y - a.y) || 1; nx = (b.x - a.x) / cd; ny = (b.y - a.y) / cd; }
  return { hit: dist <= sum, dist, depth: sum - dist, nx, ny };
}

// Voiture vs cercle (plot, bonus…) → normale de la voiture vers le cercle
export function carVsCircle(car, cx, cy, r) {
  const e = capsuleEnds(car);
  const [qx, qy] = closestOnSeg(cx, cy, e[0], e[1], e[2], e[3]);
  const dx = cx - qx, dy = cy - qy, dist = Math.hypot(dx, dy), sum = carRadius(car) + r;
  const nx = dist > 1e-6 ? dx / dist : 0, ny = dist > 1e-6 ? dy / dist : 1;
  return { hit: dist <= sum, dist, depth: sum - dist, nx, ny };
}

// Voiture vs mur (segment) → normale qui repousse la voiture hors du mur
export function carVsSegment(car, x1, y1, x2, y2) {
  const e = capsuleEnds(car);
  const [px, py, qx, qy] = segSegClosest(e[0], e[1], e[2], e[3], x1, y1, x2, y2);
  const dx = px - qx, dy = py - qy, dist = Math.hypot(dx, dy), R = carRadius(car);
  const nx = dist > 1e-6 ? dx / dist : 0, ny = dist > 1e-6 ? dy / dist : -1;
  return { hit: dist <= R, dist, depth: R - dist, nx, ny };
}

export function pointInCar(car, px, py) {
  const e = capsuleEnds(car);
  const [qx, qy] = closestOnSeg(px, py, e[0], e[1], e[2], e[3]);
  return Math.hypot(px - qx, py - qy) <= carRadius(car);
}
